/**
 * Push Notification Service
 * Handles Firebase Cloud Messaging operations including permissions, tokens, and message handling
 */

import { getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging';
import { messaging } from '@/lib/firebase';
import {
  NotificationPermissionState,
  NotificationPayload,
  FCMToken,
  NotificationSettings,
  NotificationTopics
} from '@/types/notifications';

import { getVapidKey } from './firebaseConfig.service';
import axiosInstance from '@/lib/axios';

const STORAGE_KEYS = {
  FCM_TOKEN: 'fcm_token',
  NOTIFICATION_SETTINGS: 'notification_settings',
  REGISTRATION_STATUS: 'fcm_registration_status',
} as const;

// Get VAPID key from configuration service
const getVAPIDKey = (): string => {
  try {
    return getVapidKey();
  } catch (error) {
    console.warn('VAPID key not configured, push notifications may not work:', error);
    return ''; // Fallback key
  }
};

export const pushNotificationService = {
  /**
   * Check if push notifications are supported
   */
  isSupported: async (): Promise<boolean> => {
    try {
      return await isSupported() && 'Notification' in window && 'serviceWorker' in navigator;
    } catch (error) {
      console.error('Error checking notification support:', error);
      return false;
    }
  },

  /**
   * Get current notification permission state
   */
  getPermissionState: (): NotificationPermissionState => {
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';

    const supported = 'Notification' in window;
    return {
      permission,
      isSupported: supported,
      isEnabled: permission === 'granted'
    };
  },

  /**
   * Request notification permission from user
   */
  requestPermission: async (): Promise<NotificationPermission> => {
    try {
      if (!('Notification' in window)) {
        throw new Error('This browser does not support notifications');
      }

      if (Notification.permission === 'granted') {
        return 'granted';
      }

      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);

      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  },

  /**
   * Get FCM token for this device
   */
  getToken: async (): Promise<string | null> => {
    try {
      if (!messaging) {
        console.warn('Firebase Messaging not supported');
        return null;
      }
      const permission = await pushNotificationService.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }

      // Get or register service worker
      const registration = await pushNotificationService.registerServiceWorker();

      const token = await getToken(messaging, {
        vapidKey: getVAPIDKey(),
        serviceWorkerRegistration: registration
      });

      if (token) {
        // Store token with timestamp
        const fcmToken: FCMToken = {
          token,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEYS.FCM_TOKEN, JSON.stringify(fcmToken));
        console.log('FCM Token generated and stored:', token);
        return token;
      } else {
        console.log('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  },

  /**
   * Unregister FCM Service Worker from browser
   */
  unregisterServiceWorker: async (): Promise<void> => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          if (
            registration.active?.scriptURL?.includes('firebase-messaging-sw.js') ||
            registration.scope?.includes('firebase-cloud-messaging-push-scope') ||
            registration.active?.scriptURL?.includes('firebase')
          ) {
            await registration.unregister();
            console.log('FCM Service Worker unregistered:', registration.scope);
          }
        }
      }
    } catch (error) {
      console.error('Error unregistering service worker:', error);
    }
  },

  /**
   * Delete FCM token and unregister service worker
   */
  deleteToken: async (): Promise<void> => {
    try {
      // Get stored token before deleting
      const storedToken = pushNotificationService.getStoredToken();

      // Unregister from backend if token exists
      if (storedToken?.token) {
        try {
          await pushNotificationService.unregisterTokenWithBackend(storedToken.token);
        } catch (error) {
          console.error('Failed to unregister token from backend:', error);
        }
      }

      if (messaging) {
        try {
          await deleteToken(messaging);
        } catch (err) {
          console.warn('Error deleting token from Firebase:', err);
        }
      }

      // Unregister Service Worker from browser
      await pushNotificationService.unregisterServiceWorker();

      localStorage.removeItem(STORAGE_KEYS.FCM_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_STATUS);
      console.log('FCM Token deleted and service worker unregistered');
    } catch (error) {
      console.error('Error deleting FCM token:', error);
      throw error;
    }
  },

  /**
   * Get stored FCM token
   */
  getStoredToken: (): FCMToken | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error getting stored FCM token:', error);
      return null;
    }
  },

  /**
   * Register service worker for handling background messages
   */
  registerServiceWorker: async (): Promise<ServiceWorkerRegistration | undefined> => {
    try {
      if ('serviceWorker' in navigator) {
        // Check if already registered to prevent duplicates
        const registrations = await navigator.serviceWorker.getRegistrations();
        const existingRegistration = registrations.find(
          reg => reg.scope.includes('firebase-cloud-messaging-push-scope')
        );
        
        if (existingRegistration) {
          console.log('Service Worker already registered:', existingRegistration.scope);
          return existingRegistration;
        }
        
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker registered:', registration);
        return registration;
      }
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  },

  /**
   * Set up foreground message handler
   */
  onMessage: (callback: (payload: NotificationPayload) => void): (() => void) => {
    if (!messaging) {
      console.warn('Firebase Messaging not supported');
      return () => { };
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      callback(payload);
    });

    return unsubscribe;
  },

  /**
   * Show browser notification (for foreground messages)
   */
  showNotification: (payload: NotificationPayload): void => {
    try {
      if (Notification.permission !== 'granted') {
        return;
      }

      const title = payload.notification?.title || 'Quick Assist';
      const options = {
        body: payload.notification?.body || '',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        image: payload.notification?.image || '/favicon.ico',
        data: {
          ...(payload.data || {}),
          url: payload.fcmOptions?.link || payload.data?.redirectUrl || payload.data?.url || '/'
        },
        tag: 'quick-assist-foreground',
        requireInteraction: false
      } as NotificationOptions & { image?: string };

      new Notification(title, options);
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  },

  /**
   * Get notification settings
   */
  getNotificationSettings: (): NotificationSettings => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.NOTIFICATION_SETTINGS);
      if (stored) {
        return JSON.parse(stored);
      }

      const isPermissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';

      // Default settings
      return {
        enabled: isPermissionGranted,
        topics: {
          [NotificationTopics.GENERAL]: true,
          [NotificationTopics.ALERTS]: true,
          [NotificationTopics.UPDATES]: false,
          [NotificationTopics.CHAT]: true,
          [NotificationTopics.SYSTEM]: false
        },
        sound: true,
        vibrate: true
      };
    } catch (error) {
      console.error('Error getting notification settings:', error);
      const isPermissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      return {
        enabled: isPermissionGranted,
        topics: {
          [NotificationTopics.GENERAL]: true,
          [NotificationTopics.ALERTS]: true,
          [NotificationTopics.UPDATES]: false,
          [NotificationTopics.CHAT]: true,
          [NotificationTopics.SYSTEM]: false
        },
        sound: true,
        vibrate: true
      };
    }
  },

  /**
   * Update notification settings
   */
  updateNotificationSettings: (settings: NotificationSettings): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.NOTIFICATION_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('Error updating notification settings:', error);
    }
  },

  /**
   * Send token to backend for registration
   */
  registerTokenWithBackend: async (token: string, userId?: string): Promise<void> => {
    try {
      // Check if this token is already registered for this user
      const registrationKey = `${token}-${userId || 'guest'}`;
      const lastRegistration = localStorage.getItem(STORAGE_KEYS.REGISTRATION_STATUS);
      const registrationData = lastRegistration ? JSON.parse(lastRegistration) : {};
      
      // Skip if already registered within the last hour
      const lastRegistrationTime = registrationData[registrationKey];
      if (lastRegistrationTime && Date.now() - lastRegistrationTime < 60 * 60 * 1000) {
        console.log('Token already registered recently, skipping backend registration');
        return;
      }

      await axiosInstance.post('/auth/notifications/register', {
        token,
        platform: 'web'
      });
      
      // Mark as registered
      registrationData[registrationKey] = Date.now();
      localStorage.setItem(STORAGE_KEYS.REGISTRATION_STATUS, JSON.stringify(registrationData));
      
      console.log('FCM token registered with backend successfully');
    } catch (error) {
      console.error('Error registering token with backend:', error);
      throw error;
    }
  },



  unregisterTokenWithBackend: async (token: string): Promise<void> => {
    try {
      await axiosInstance.post(`/auth/notifications/unregister`, {
      token
    });
      console.log('FCM token unregistered from backend successfully');
    } catch (error) {
      console.error('Error unregistering token from backend:', error);
      throw error;
    }
  },

  /**
   * Initialize push notifications
   */
  initialize: async (userId?: string): Promise<string | null> => {
    try {
      // Check if already initializing to prevent race conditions
      const initKey = `init-${userId || 'anonymous'}`;
      const lastInit = localStorage.getItem(`push-init-${initKey}`);
      const now = Date.now();
      
      // Prevent multiple simultaneous initializations (within 30 seconds)
      if (lastInit && now - parseInt(lastInit) < 30000) {
        console.log('Push notification initialization already in progress, skipping');
        const existingToken = pushNotificationService.getStoredToken();
        return existingToken?.token || null;
      }
      
      localStorage.setItem(`push-init-${initKey}`, now.toString());

      const isSupported = await pushNotificationService.isSupported();
      if (!isSupported) {
        console.log('Push notifications not supported');
        localStorage.removeItem(`push-init-${initKey}`);
        return null;
      }

      // Check if we already have a valid token
      const existingToken = pushNotificationService.getStoredToken();
      if (existingToken) {
        console.log('Using existing FCM token:', existingToken.token);
        // Only register with backend if userId is provided and we haven't registered recently
        if (userId) {
          const registrationKey = `${existingToken.token}-${userId}`;
          const lastRegistration = localStorage.getItem(STORAGE_KEYS.REGISTRATION_STATUS);
          const registrationData = lastRegistration ? JSON.parse(lastRegistration) : {};
          const lastRegistrationTime = registrationData[registrationKey];
          
          // Only re-register if it's been more than 1 hour since last registration
          if (!lastRegistrationTime || now - lastRegistrationTime > 60 * 60 * 1000) {
            console.log('Re-registering token with backend for user:', userId);
            await pushNotificationService.registerTokenWithBackend(existingToken.token, userId);
          } else {
            console.log('Token already registered recently, skipping backend registration');
          }
        }
        localStorage.removeItem(`push-init-${initKey}`);
        return existingToken.token;
      }

      // Generate new token if none exists
      const token = await pushNotificationService.getToken();
      if (token && userId) {
        console.log('New token generated, registering with backend for user:', userId);
        await pushNotificationService.registerTokenWithBackend(token, userId);
      }

      localStorage.removeItem(`push-init-${initKey}`);
      return token;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      // Clean up initialization flag on error
      const initKey = `init-${userId || 'anonymous'}`;
      localStorage.removeItem(`push-init-${initKey}`);
      return null;
    }
  },

  /**
   * Clean up push notification service
   */
  cleanup: async (): Promise<void> => {
    try {
      await pushNotificationService.deleteToken();
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_STATUS);
      console.log('Push notification service cleaned up');
    } catch (error) {
      console.error('Error cleaning up push notification service:', error);
    }
  }
};